import {
  AlertTriangle,
  HeartPulse,
  KeyRound,
  Lock,
  MapPinned,
  QrCode,
  RefreshCw,
  ScrollText,
  Smartphone,
  UserPlus,
  Users,
  XCircle,
} from "lucide-react";
import QRCode from "qrcode";
import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { bytes, sealSecretTo } from "@tether/crypto";
import { useSession } from "../App.js";
import { api, type VaultDetail } from "../lib/api.js";
import { loadGroupKey, openSealed } from "../lib/keys.js";
import { Spinner } from "../lib/Spinner.js";

export function Vault() {
  const { vaultId = "" } = useParams();
  const { me } = useSession();
  const nav = useNavigate();
  const [d, setD] = useState<VaultDetail | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [invite, setInvite] = useState<{
    name: string;
    inviteUrl: string;
    code: string;
    expiresAt: string;
  } | null>(null);
  const [inviteName, setInviteName] = useState("");
  const [pairing, setPairing] = useState<{
    code: string;
    qr: string;
    expiresAt: string;
    name: string;
  } | null>(null);
  const [pairName, setPairName] = useState("");
  const [busy, setBusy] = useState(false);
  const [audit, setAudit] = useState<
    {
      id: string;
      ts: number;
      kind: string;
      actor: { name?: string; deviceId?: string; admin?: boolean };
      data?: Record<string, unknown>;
      verified: boolean;
    }[]
  >([]);

  // Vault health/devices are cheap (registry-only) and auto-refresh every 10s. Audit history is not
  // — it lists the bucket, which the gateway would otherwise pay for on every poll tick — so it's
  // loaded once on mount and otherwise only on an explicit "Refresh" click.
  const [auditBusy, setAuditBusy] = useState(false);
  const load = useCallback(async () => {
    try {
      setD(await api<VaultDetail>(`/v1/vaults/${vaultId}`));
    } catch (e) {
      setErr((e as Error).message);
    }
  }, [vaultId]);
  const loadAudit = useCallback(async () => {
    setAuditBusy(true);
    try {
      const a = await api<{ entries: typeof audit }>(
        `/v1/vaults/${vaultId}/audit?from=${Date.now() - 30 * 86_400_000}&to=${Date.now() + 60_000}`,
      );
      setAudit(a.entries.slice(-50).reverse());
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setAuditBusy(false);
    }
  }, [vaultId]);
  useEffect(() => {
    void load();
    void loadAudit();
    const t = setInterval(() => {
      if (!document.hidden) void load();
    }, 10_000);
    return () => clearInterval(t);
  }, [load, loadAudit]);

  const mine = me?.vaults.find((v) => v.id === vaultId);
  const isOwner = mine?.role === "owner";

  async function createInvite() {
    setBusy(true);
    setErr(null);
    try {
      const r = await api<{ inviteUrl: string; code: string; expiresAt: string }>(
        `/v1/vaults/${vaultId}/confidants`,
        { body: { name: inviteName } },
      );
      setInvite({ name: inviteName, ...r });
      setInviteName("");
      await load();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  }
  async function removeInvite(id: string) {
    await api(`/v1/vaults/${vaultId}/confidants/${id}`, { method: "DELETE" });
    await load();
  }
  async function verify(c: VaultDetail["confidants"][number]) {
    if (!me || !mine || !c.groupPublicKey) return;
    setBusy(true);
    setErr(null);
    try {
      const kp = loadGroupKey(me.user.id);
      if (!kp || !mine.sealedMetadataSecret)
        throw new Error("Your browser key for this vault is missing; import it from your backup.");
      const msk = openSealed(kp, mine.sealedMetadataSecret, "msk");
      const sealed = sealSecretTo(bytes.fromBase64Url(c.groupPublicKey), msk, "msk");
      msk.fill(0);
      await api(`/v1/vaults/${vaultId}/confidants/${c.id}/verify`, {
        body: { sealedMetadataSecret: bytes.toBase64Url(sealed) },
      });
      await load();
      void loadAudit();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  }
  async function createPairing() {
    setBusy(true);
    setErr(null);
    try {
      const r = await api<{ code: string; expiresAt: string; payload: unknown }>(
        `/v1/vaults/${vaultId}/pairings`,
        { body: { name: pairName } },
      );
      const qr = await QRCode.toDataURL(JSON.stringify(r.payload), { width: 240, margin: 1 });
      setPairing({ code: r.code, qr, expiresAt: r.expiresAt, name: pairName });
      setPairName("");
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  }
  async function revoke(id: string) {
    if (
      !confirm("Revoke this device? It will stop streaming immediately and must be paired again.")
    )
      return;
    await api(`/v1/vaults/${vaultId}/devices/${id}/revoke`, { body: {} });
    await load();
    void loadAudit();
  }
  async function deleteVault() {
    if (!d) return;
    setDeleting(true);
    setErr(null);
    try {
      await api(`/v1/vaults/${vaultId}`, {
        method: "DELETE",
        body: { confirmName: deleteConfirm },
      });
      nav("/", { replace: true });
    } catch (e) {
      setErr((e as Error).message);
      setDeleting(false);
    }
  }

  if (!d) return err ? <p className="error">{err}</p> : <Spinner label="Loading…" />;
  const h = d.health;
  return (
    <div>
      <div className="row" style={{ justifyContent: "space-between" }}>
        <h2 style={{ margin: "0 0 12px" }}>{d.vault.name}</h2>
        <div className="row">
          <Link to={`/vaults/${vaultId}/timeline`}>
            <button style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <MapPinned size={15} />
              Timeline &amp; map
            </button>
          </Link>
          {d.vault.mode === "kid" && (
            <Link to={`/vaults/${vaultId}/decrypt`}>
              <button className="primary" style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <Lock size={15} />
                Decrypt recordings
              </button>
            </Link>
          )}
        </div>
      </div>
      {err && <p className="error">{err}</p>}

      <div className="card">
        <h2 style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <HeartPulse size={18} className="icon" />
          Vault health
        </h2>
        <p className="muted" style={{ marginTop: 0 }}>
          Mode: <strong>{d.vault.mode}</strong>{" "}
          {d.vault.mode === "kid" &&
            "· you can view recordings any time; your child's device is notified when you do"}
          {d.vault.mode === "guardian" && "· one designated co-owner approves an unlock"}
          {d.vault.mode === "circle" && `· ${d.vault.k} of ${d.vault.n} confidants must approve`}
        </p>
        <div className="grid">
          <div className="stat">
            <div className="big">{h.sealed ? "Sealed" : "Open"}</div>
            <div className="muted">
              {h.sealed
                ? d.vault.mode === "kid"
                  ? "Recordings are sealed to you."
                  : "The gateway holds too few shares to open anything."
                : `${h.unclaimedShares} share(s) still waiting to be claimed.`}
            </div>
          </div>
          {d.vault.mode !== "kid" && (
            <div className="stat">
              <div className="big">
                {h.enrolled} / {h.total}
              </div>
              <div className="muted">
                {d.vault.mode === "guardian" ? "co-owner enrolled" : "confidants enrolled"} ·{" "}
                {h.needed} needed to unlock
              </div>
            </div>
          )}
          <div className="stat">
            <div className="big">{d.devices.filter((x) => x.connected).length}</div>
            <div className="muted">device(s) streaming now</div>
          </div>
        </div>
        <p style={{ marginTop: 12 }}>
          <span className={`badge ${h.ok ? "ok" : "warn"}`}>
            {h.ok
              ? "Ready: an emergency unlock is possible"
              : d.vault.mode === "guardian"
                ? "Not ready: enroll your co-owner"
                : "Not ready: enroll more confidants"}
          </span>
        </p>
      </div>

      {d.vault.mode !== "kid" && (
        <div className="card">
          <h2 style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Users size={18} className="icon" />
            {d.vault.mode === "guardian" ? "Co-owner" : "Confidants"}
          </h2>
          <p className="muted">
            {d.vault.mode === "guardian"
              ? "The one designated person who can approve an emergency unlock on your behalf. Holds one share on their own phone or browser."
              : `People who can approve an emergency unlock. Each holds one share on their own phone or browser. ${d.vault.k} of ${d.vault.n} must approve.`}
          </p>
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Status</th>
                <th>Safety number</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {d.confidants.map((c) => (
                <tr key={c.id}>
                  <td>
                    {c.name}
                    <div className="muted">slot {c.shareIndex}</div>
                  </td>
                  <td>
                    <span
                      className={`badge ${c.status === "verified" ? "ok" : c.status === "enrolled" ? "warn" : ""}`}
                    >
                      {c.status}
                    </span>
                    {c.lastSeenAt && (
                      <div className="muted">seen {new Date(c.lastSeenAt).toLocaleString()}</div>
                    )}
                  </td>
                  <td>
                    {c.fingerprint ? (
                      <div className="fingerprint">{c.fingerprint}</div>
                    ) : (
                      <span className="muted">not yet enrolled</span>
                    )}
                  </td>
                  <td>
                    {isOwner && c.status === "enrolled" && (
                      <button
                        className="primary"
                        onClick={() => verify(c)}
                        disabled={busy}
                        title="Compare the safety number with them in person, then confirm"
                        style={{ display: "flex", alignItems: "center", gap: 6 }}
                      >
                        <KeyRound size={14} />
                        Numbers match
                      </button>
                    )}
                    {isOwner && c.status === "invited" && (
                      <button
                        className="danger"
                        onClick={() => removeInvite(c.id)}
                        style={{ display: "flex", alignItems: "center", gap: 6 }}
                      >
                        <XCircle size={14} />
                        Cancel invite
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {isOwner && d.confidants.length < d.vault.n && (
            <div className="row" style={{ marginTop: 12 }}>
              <input
                style={{ maxWidth: 260 }}
                placeholder={d.vault.mode === "guardian" ? "Co-owner's name" : "Confidant's name"}
                value={inviteName}
                onChange={(e) => setInviteName(e.target.value)}
              />
              <button
                className="primary"
                onClick={createInvite}
                disabled={busy || !inviteName}
                style={{ display: "flex", alignItems: "center", gap: 6 }}
              >
                <UserPlus size={15} />
                {d.vault.mode === "guardian" ? "Invite co-owner" : "Invite"}
              </button>
            </div>
          )}
          {invite && (
            <div className="card" style={{ marginTop: 12 }}>
              <h3>Invitation for {invite.name}</h3>
              <p>
                Send them this link: <code>{invite.inviteUrl}</code>
              </p>
              <p>
                Tell them this code <strong>in person or by voice</strong>, never in the same
                message as the link:
              </p>
              <div className="code">{invite.code}</div>
              <p className="muted">
                Expires {new Date(invite.expiresAt).toLocaleString()}. Afterwards, compare safety
                numbers and press “Numbers match”.
              </p>
            </div>
          )}
        </div>
      )}

      <div className="card">
        <h2 style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Smartphone size={18} className="icon" />
          Devices
        </h2>
        <table>
          <thead>
            <tr>
              <th>Name</th>
              <th>Capabilities</th>
              <th>Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {d.devices.map((x) => (
              <tr key={x.id}>
                <td>{x.name}</td>
                <td>{x.caps.join(", ")}</td>
                <td>
                  {x.revokedAt ? (
                    <span className="badge bad">revoked</span>
                  ) : x.connected ? (
                    <span className="badge ok">streaming</span>
                  ) : (
                    <span className="badge">
                      {x.lastSeenAt
                        ? `last seen ${new Date(x.lastSeenAt).toLocaleString()}`
                        : "never connected"}
                    </span>
                  )}
                </td>
                <td>
                  {isOwner && !x.revokedAt && (
                    <button
                      className="danger"
                      onClick={() => revoke(x.id)}
                      style={{ display: "flex", alignItems: "center", gap: 6 }}
                    >
                      <XCircle size={14} />
                      Revoke
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {isOwner && (
          <div className="row" style={{ marginTop: 12 }}>
            <input
              style={{ maxWidth: 260 }}
              placeholder="Device name (e.g. Maya's phone)"
              value={pairName}
              onChange={(e) => setPairName(e.target.value)}
            />
            <button
              className="primary"
              onClick={createPairing}
              disabled={busy || !pairName}
              style={{ display: "flex", alignItems: "center", gap: 6 }}
            >
              <QrCode size={15} />
              Pair a device
            </button>
          </div>
        )}
        {pairing && (
          <div className="card" style={{ marginTop: 12 }}>
            <h3>Pair “{pairing.name}”</h3>
            <div className="row">
              <img src={pairing.qr} alt="pairing QR code" width={200} height={200} />
              <div>
                <p>Scan with the Tether app, or enter the code:</p>
                <div className="code">{pairing.code}</div>
                <p className="muted">
                  Single use. Expires {new Date(pairing.expiresAt).toLocaleTimeString()}.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
      <div className="card">
        <div className="row" style={{ justifyContent: "space-between" }}>
          <h2 style={{ margin: 0, display: "flex", alignItems: "center", gap: 8 }}>
            <ScrollText size={18} className="icon" />
            Audit log
          </h2>
          <button
            onClick={() => void loadAudit()}
            disabled={auditBusy}
            style={{ display: "flex", alignItems: "center", gap: 6 }}
          >
            <RefreshCw size={14} className={auditBusy ? "spin" : ""} />
            {auditBusy ? "Refreshing…" : "Refresh"}
          </button>
        </div>
        <p className="muted">Every step is signed by the gateway. Last 30 days.</p>
        {audit.length === 0 && <p className="muted">Nothing yet.</p>}
        <ul className="list-plain">
          {audit.map((e) => (
            <li key={e.id}>
              <strong>{e.kind}</strong> · {new Date(e.ts).toLocaleString()} ·{" "}
              <span className="muted">
                {e.actor.name ?? e.actor.deviceId ?? (e.actor.admin ? "admin" : "")}
              </span>
              {e.data && <span className="muted"> · {JSON.stringify(e.data)}</span>}
              {!e.verified && <span className="badge bad"> signature invalid</span>}
            </li>
          ))}
        </ul>
      </div>

      {isOwner && (
        <div className="card danger">
          <h2 style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <AlertTriangle size={18} className="icon" />
            Danger zone
          </h2>
          <p>
            Deleting <strong>{d.vault.name}</strong> permanently destroys every recording, key, and
            confidant enrollment in it. This cannot be undone, and no one — including you — will be
            able to recover anything afterward.
          </p>
          <label>
            Type <strong>{d.vault.name}</strong> to confirm
          </label>
          <input
            value={deleteConfirm}
            onChange={(e) => setDeleteConfirm(e.target.value)}
            placeholder={d.vault.name}
            autoComplete="off"
          />
          <div className="row" style={{ marginTop: 12 }}>
            <button
              className="danger"
              onClick={deleteVault}
              disabled={deleting || deleteConfirm !== d.vault.name}
            >
              {deleting ? "Deleting…" : "Delete this vault forever"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
